import { supabase } from "@/integrations/supabase/client";

export type BnplProvider = 'tabby' | 'tamara';

export interface BnplCustomer {
  name: string;
  email: string;
  phone?: string;
}

export interface BnplCheckoutParams {
  provider: BnplProvider;
  orderId: string;
  amount: number;
  currency?: string;
  customer: BnplCustomer;
}

/**
 * Creates a BNPL checkout session via Supabase edge function.
 * Expects Supabase function `provider-checkout` to return { redirectUrl: string }.
 */
export async function createBnplCheckoutSession(
  params: BnplCheckoutParams
): Promise<string> {
  const { data, error } = await supabase.functions.invoke('provider-checkout', {
    body: {
      provider: params.provider,
      orderId: params.orderId,
      amount: params.amount,
      currency: params.currency ?? 'AED',
      customer: params.customer,
    },
  });

  if (error) {
    console.error('BNPL checkout error', error);
    throw new Error(error.message ?? 'Failed to start BNPL checkout');
  }

  if (!data || typeof data.redirectUrl !== 'string') {
    console.error('BNPL checkout missing redirectUrl', data);
    throw new Error('BNPL provider did not return a redirect URL');
  }

  return data.redirectUrl;
}

type TravelHoldParams = {
  offerId: string;
  passengers: any;
  totalAmount: number; // Required: total price from the offer
  contact: {
    name: string;
    email: string;
    phone?: string;
  };
};

/**
 * 1) Calls duffel-orders-hold edge function to create a hold in Duffel
 * 2) Creates a lead with customer info
 * 3) Inserts an order into the orders table
 * 4) Returns the new orderId for redirecting to /travel/checkout
 */
export async function createTravelOrder(
  params: TravelHoldParams
): Promise<{ orderId: string }> {
  // Step 1 – call duffel-orders-hold
  const { data: holdData, error: holdError } = await supabase.functions.invoke(
    'duffel-orders-hold',
    {
      body: {
        offer_id: params.offerId,
        passengers: params.passengers,
      },
    }
  );

  if (holdError) {
    console.error('duffel-orders-hold error', holdError);
    throw new Error(holdError.message ?? 'Could not create Duffel order hold');
  }

  const duffelOrderId = holdData?.order_id ?? holdData?.orderId;
  const currency = holdData?.currency ?? 'AED';
  
  // Use the totalAmount passed from the offer (not from hold response)
  const totalAmount = params.totalAmount;

  if (!duffelOrderId) {
    console.error('No Duffel order ID returned from hold', holdData);
    throw new Error('Duffel hold did not return an order ID');
  }

  // Step 2 – create a lead with customer info
  const { data: leadRow, error: leadError } = await supabase
    .from('leads')
    .insert({
      name: params.contact.name,
      email: params.contact.email,
      phone: params.contact.phone,
      status: 'contacted',
    })
    .select('id')
    .single();

  if (leadError) {
    console.error('leads insert error', leadError);
    throw new Error(leadError.message ?? 'Could not create lead');
  }

  // Step 3 – insert into orders table with Duffel order ID and customer email
  const { data: orderRow, error: orderError } = await supabase
    .from('orders')
    .insert({
      amount_aed: totalAmount,
      currency,
      status: 'pending',
      lead_id: leadRow.id,
      duffel_order_id: duffelOrderId,
      customer_email: params.contact.email,
    })
    .select('id')
    .single();

  if (orderError) {
    console.error('orders insert error', orderError);
    throw new Error(orderError.message ?? 'Could not create order');
  }

  return { orderId: orderRow.id as string };
}